import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Settings as SettingsApi, appBase } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { FileSignature, RotateCcw, Eye, EyeOff } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const TAGS = ['{{fullTitle}}', '{{shortShow}}', '{{showName}}', '{{legalEntity}}', '{{legalShort}}'];

function renderPreview(text, show) {
  const full = show?.fullTitle || show?.name || 'the Podcast';
  const map = {
    '{{fullTitle}}': full, '{{showName}}': show?.name || full, '{{shortShow}}': full.split(' with')[0],
    '{{legalEntity}}': show?.legalEntity || 'WineTalk LLC', '{{legalShort}}': show?.legalShort || 'WineTalk',
  };
  let out = text;
  Object.entries(map).forEach(([k, v]) => { out = out.split(k).join(v); });
  return out.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
}

export function ReleaseTextSettings({ settings, shows = [], onSaved }) {
  const [defaults, setDefaults] = useState(null);
  const [intro, setIntro] = useState('');
  const [clauses, setClauses] = useState('');
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const show = shows[0];

  useEffect(() => {
    axios.get(`${appBase}/api/release-text/defaults`).then(r => {
      setDefaults(r.data);
      setIntro(settings?.releaseIntro || r.data.intro);
      setClauses(settings?.releaseClauses || r.data.clauses);
    }).catch(() => {});
  }, [settings?.releaseIntro, settings?.releaseClauses]);

  const isDefault = defaults && intro === defaults.intro && clauses === defaults.clauses;

  const save = async () => {
    setSaving(true);
    try {
      await SettingsApi.update({
        releaseIntro: defaults && intro.trim() === defaults.intro ? '' : intro,
        releaseClauses: defaults && clauses.trim() === defaults.clauses ? '' : clauses,
      });
      toast({ title: 'Release form text saved', description: 'New guests will see the updated wording; already-signed forms keep the text they agreed to.' });
      onSaved?.();
    } catch (e) { toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  const reset = () => { if (defaults) { setIntro(defaults.intro); setClauses(defaults.clauses); } };

  return (
    <Card className="border-stone-200 mb-4" data-testid="release-text-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-[15px] flex items-center gap-2">
          <FileSignature className="w-4 h-4 text-stone-500" /> Release form wording
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-[12px] text-stone-600">
          This text appears on the public release form guests sign and in the signed PDF. Separate clauses with a blank line. Merge tags fill in per show:
          {' '}{TAGS.map(t => <code key={t} className="bg-stone-100 px-1 rounded mr-1 text-[11px]">{t}</code>)}
        </div>
        <div>
          <Label className="text-[11px] uppercase text-stone-500">Intro line</Label>
          <Input data-testid="release-intro-input" value={intro} onChange={(e) => setIntro(e.target.value)} />
        </div>
        <div>
          <Label className="text-[11px] uppercase text-stone-500">Clauses</Label>
          <Textarea data-testid="release-clauses-input" value={clauses} onChange={(e) => setClauses(e.target.value)} rows={16} className="text-[12.5px] leading-relaxed" />
        </div>
        {preview && (
          <div className="bg-stone-50 border border-stone-200 rounded-md p-4 space-y-3 text-[13px] leading-relaxed text-stone-700" data-testid="release-preview">
            <div className="text-[11px] uppercase text-stone-500">Preview — {show?.name}</div>
            <p className="text-stone-600">{renderPreview(intro, show)[0] || ''}</p>
            {renderPreview(clauses, show).map((p, i) => <p key={i}>{p}</p>)}
          </div>
        )}
        <div className="flex flex-wrap justify-between gap-2">
          <div className="flex gap-2">
            <Button data-testid="release-preview-toggle" variant="ghost" size="sm" onClick={() => setPreview(p => !p)}>
              {preview ? <EyeOff className="w-3.5 h-3.5 mr-1.5" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />} {preview ? 'Hide preview' : 'Preview as guest sees it'}
            </Button>
            <Button data-testid="release-reset-btn" variant="ghost" size="sm" onClick={reset} disabled={isDefault}>
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset to original
            </Button>
          </div>
          <Button data-testid="release-save-btn" onClick={save} disabled={saving || !clauses.trim()} className="bg-stone-900 hover:bg-stone-800 text-white">
            {saving ? 'Saving…' : 'Save release wording'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
